import React, { useState } from 'react';
import { CreditCard, Plus, Trash2, LogIn, LogOut, CheckCircle, Shield, AlertTriangle, Printer } from 'lucide-react';
import { RealQRCode, qrEncodeToCanvas } from '../../utils/qr';
import {
  createVisitorCards, deleteVisitorCard,
  signInVisitorCard, signOutVisitorCard,
  markVisitorSafe, markVisitorEvacuated,
  lookupVisitor,
} from '../../api/visitors';
import { listVisitRequests, getVisitRequest } from '../../api/visitors';

const C = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', teal: '#009688', orange: '#f97316', purple: '#a78bfa',
};

const EMERGENCY_COLORS = { NOT_RESPONDED: C.muted, SAFE: C.green, NEED_HELP: C.red, EVACUATED: C.amber };

// ── Visitor Card Create Modal ────────────────────────────────
function CardCreateModal({ sites, siteId: defaultSiteId, onSave, onClose }) {
  const [form, setForm] = useState({ siteId: defaultSiteId || '', mode: 'bulk', count: 10, cardNumber: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const selectedSite = sites.find(s => s.id === form.siteId);
  const autoPrefix = selectedSite ? `${selectedSite.name} V` : 'V';

  const previewTo = Math.min(form.count, 200);
  const previewStart = String(1).padStart(3, '0');
  const previewEnd = String(previewTo).padStart(3, '0');

  async function handleSave() {
    if (!form.siteId) return setErr('Site is required');
    setSaving(true); setErr('');
    try {
      const body = form.mode === 'bulk'
        ? { siteId: form.siteId, count: parseInt(form.count, 10), prefix: autoPrefix }
        : { siteId: form.siteId, cardNumber: form.cardNumber.trim() };
      const created = await createVisitorCards(body);
      onSave(created);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to create'); }
    setSaving(false);
  }

  const inputSt = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelSt = { fontSize: '11px', fontWeight: 600, color: C.muted, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '420px', maxWidth: '95vw' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '20px' }}>Create Visitor Cards</div>
        {err && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: C.red }}>{err}</div>}

        <div style={{ marginBottom: '14px' }}>
          <label style={labelSt}>Site *</label>
          <select style={inputSt} value={form.siteId} onChange={e => setForm(f => ({ ...f, siteId: e.target.value }))}>
            <option value="">— Select site —</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {['bulk', 'single'].map(m => (
            <button key={m} onClick={() => setForm(f => ({ ...f, mode: m }))} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${form.mode === m ? C.teal : C.border}`, background: form.mode === m ? C.teal + '22' : 'transparent', color: form.mode === m ? C.teal : C.muted, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {m === 'bulk' ? 'Bulk (numbered)' : 'Single card'}
            </button>
          ))}
        </div>

        {form.mode === 'bulk' ? (<>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelSt}>Number of Cards (max 200)</label>
            <input type="number" min={1} max={200} style={inputSt} value={form.count} onChange={e => setForm(f => ({ ...f, count: e.target.value }))} />
          </div>
          <div style={{ background: C.bg, borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: C.subtle, marginBottom: '14px' }}>
            Will create: <strong style={{ color: C.teal }}>{autoPrefix}{previewStart}</strong> … <strong style={{ color: C.teal }}>{autoPrefix}{previewEnd}</strong>
          </div>
        </>) : (
          <div style={{ marginBottom: '14px' }}>
            <label style={labelSt}>Card Number</label>
            <input style={inputSt} placeholder={selectedSite ? `e.g. ${selectedSite.name} V001` : 'e.g. Dubai HQ V001'} value={form.cardNumber} onChange={e => setForm(f => ({ ...f, cardNumber: e.target.value }))} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.teal, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Visitor Sign In Modal ────────────────────────────────────
function SignInModal({ card, siteId, zones, assignedPersonnelIds, onSave, onClose }) {
  const [step, setStep] = useState(1);
  const [visitorIdInput, setVisitorIdInput] = useState('');
  const [lookedUpVisitor, setLookedUpVisitor] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState('');
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [selectedVR, setSelectedVR] = useState(null);
  const [vrDetail, setVrDetail] = useState(null);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState('');
  const [allowedZones, setAllowedZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const siteZones = zones.filter(z => z.siteId === siteId || z.site_id === siteId);

  React.useEffect(() => {
    listVisitRequests({ status: 'APPROVED', siteId }).then(data => { setRequests(data); setLoadingReqs(false); }).catch(() => setLoadingReqs(false));
  }, [siteId]);

  async function selectVR(vr) {
    setSelectedVR(vr);
    setSelectedPersonnelId('');
    const detail = await getVisitRequest(vr.id);
    setVrDetail(detail);
  }

  function toggleZone(zoneId) {
    setAllowedZones(z => z.includes(zoneId) ? z.filter(x => x !== zoneId) : [...z, zoneId]);
  }

  async function handleConfirm() {
    if (!selectedPersonnelId) return setErr('Please select a person');
    setSaving(true); setErr('');
    try {
      const body = {
        visitRequestId: selectedVR?.id || selectedVR,
        personnelId: selectedPersonnelId,
        allowedZoneIds: allowedZones,
      };
      if (lookedUpVisitor) {
        body.existingVisitorId = lookedUpVisitor.id;
      }
      if (photoDataUrl) {
        body.photoDataUrl = photoDataUrl;
      }
      if (!lookedUpVisitor) {
        const person = vrDetail?.personnel?.find?.(p => p.id === selectedPersonnelId);
        if (person) {
          body.newVisitor = { name: person.fullName || person.full_name || person.name };
        }
      }
      const updated = await signInVisitorCard(card.id, body);
      onSave(updated);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to sign in'); }
    setSaving(false);
  }

  const inputSt = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelSt = { fontSize: '11px', fontWeight: 600, color: C.muted, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '500px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <LogIn size={18} color={C.green} />
          <div style={{ fontSize: '15px', fontWeight: 700, color: C.text }}>Sign In — {card.cardNumber}</div>
        </div>
        <div style={{ fontSize: '12px', color: C.muted, marginBottom: '20px' }}>{card.siteName}</div>

        {/* Step tabs */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${step === s ? C.teal : C.border}`, background: step === s ? C.teal + '22' : 'transparent', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: step === s ? C.teal : C.muted, cursor: s < step ? 'pointer' : 'default' }} onClick={() => s < step && setStep(s)}>
              Step {s}: {s === 1 ? 'Select Person' : s === 2 ? 'Visitor Identity' : 'Allowed Zones'}
            </div>
          ))}
        </div>

        {err && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: C.red }}>{err}</div>}

        {step === 1 && (
          <div>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelSt}>Select Approved Visit Request</label>
              {loadingReqs ? <div style={{ color: C.muted, fontSize: '13px' }}>Loading…</div> :
                requests.length === 0 ? <div style={{ color: C.amber, fontSize: '13px' }}>No approved visit requests for this site.</div> :
                <select style={inputSt} value={selectedVR?.id || ''} onChange={e => { const vr = requests.find(r => r.id === e.target.value); if (vr) selectVR(vr); else setSelectedVR(null); }}>
                  <option value="">— Select visit request —</option>
                  {requests.map(vr => <option key={vr.id} value={vr.id}>{vr.visitorCompanyName} · {vr.arrivalDate?.slice(0,10)} – {vr.departureDate?.slice(0,10)}</option>)}
                </select>
              }
            </div>

            {vrDetail && (
              <div style={{ marginBottom: '14px' }}>
                <label style={labelSt}>Select Person</label>
                {vrDetail.personnel?.length === 0 ? <div style={{ color: C.muted, fontSize: '13px' }}>No personnel in this request.</div> :
                  (() => {
                    const available = vrDetail.personnel?.filter(p => !assignedPersonnelIds?.has(p.id));
                    return available?.length === 0
                      ? <div style={{ color: C.amber, fontSize: '13px' }}>All personnel in this request have already been assigned cards.</div>
                      : <select style={inputSt} value={selectedPersonnelId} onChange={e => setSelectedPersonnelId(e.target.value)}>
                          <option value="">— Select person —</option>
                          {available?.map(p => <option key={p.id} value={p.id}>{p.fullName}{p.jobTitle ? ` · ${p.jobTitle}` : ''}</option>)}
                        </select>;
                  })()
                }
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
              <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={() => { if (!selectedVR || !selectedPersonnelId) { setErr('Select a visit request and a person'); return; } setErr(''); setStep(2); setLookupError(''); }} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.teal, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Next →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px' }}>
              If the visitor has a permanent ID from a previous visit, enter it below. Otherwise leave blank to create a new identity.
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: C.muted, marginBottom: '5px', fontWeight: 600 }}>Existing Visitor ID (optional)</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  value={visitorIdInput}
                  onChange={e => { setVisitorIdInput(e.target.value.toUpperCase()); setLookedUpVisitor(null); setLookupError(''); }}
                  placeholder="vis-XXXX"
                  style={{ flex: 1, background: C.elevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '9px 12px', color: C.text, fontSize: '13px', outline: 'none', fontFamily: 'inherit' }}
                />
                <button
                  type="button"
                  disabled={!visitorIdInput.trim() || lookingUp}
                  onClick={async () => {
                    setLookingUp(true); setLookupError(''); setLookedUpVisitor(null);
                    try {
                      const v = await lookupVisitor(visitorIdInput.trim().toUpperCase());
                      setLookedUpVisitor(v);
                    } catch {
                      setLookupError('Visitor ID not found.');
                    }
                    setLookingUp(false);
                  }}
                  style={{ padding: '9px 14px', borderRadius: '8px', border: `1px solid ${C.green}55`, background: `${C.green}22`, color: C.green, fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
                >
                  {lookingUp ? '…' : 'Lookup'}
                </button>
              </div>
              {lookupError && <div style={{ fontSize: '11px', color: C.red, marginTop: '4px' }}>{lookupError}</div>}
              {lookedUpVisitor && (
                <div style={{ marginTop: '8px', background: `${C.green}11`, border: `1px solid ${C.green}33`, borderRadius: '6px', padding: '8px 10px', fontSize: '12px', color: C.green }}>
                  ✓ Found: <strong>{lookedUpVisitor.name}</strong>{lookedUpVisitor.employer ? ` · ${lookedUpVisitor.employer}` : ''}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: C.muted, marginBottom: '5px', fontWeight: 600 }}>Visitor Photo (optional)</label>
              {!photoDataUrl ? (
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'user';
                    input.onchange = e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => setPhotoDataUrl(ev.target.result);
                      reader.readAsDataURL(file);
                    };
                    input.click();
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px dashed ${C.border}`, background: 'transparent', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  + Capture / Upload Photo
                </button>
              ) : (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={photoDataUrl} alt="visitor" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: `1px solid ${C.border}` }} />
                  <button type="button" onClick={() => setPhotoDataUrl('')} style={{ position: 'absolute', top: '-6px', right: '-6px', background: C.red, border: 'none', borderRadius: '50%', width: '18px', height: '18px', color: '#fff', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>×</button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
              <button type="button" onClick={() => setStep(1)} style={{ padding: '9px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
              <button type="button" onClick={() => setStep(3)} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.teal, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                {lookedUpVisitor ? 'Use This Identity →' : 'Create New Identity →'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <label style={labelSt}>Allowed Zones (check all zones this visitor may enter)</label>
            {siteZones.length === 0 ? <div style={{ color: C.muted, fontSize: '13px' }}>No zones defined for this site.</div> :
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {siteZones.map(z => (
                  <label key={z.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${allowedZones.includes(z.id) ? C.teal + '66' : C.border}`, background: allowedZones.includes(z.id) ? C.teal + '11' : C.bg, cursor: 'pointer' }}>
                    <input type="checkbox" checked={allowedZones.includes(z.id)} onChange={() => toggleZone(z.id)} style={{ accentColor: C.teal, width: '16px', height: '16px' }} />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{z.name}</div>
                      {z.floor && <div style={{ fontSize: '11px', color: C.muted }}>Floor {z.floor}</div>}
                    </div>
                  </label>
                ))}
              </div>
            }
            <div style={{ fontSize: '12px', color: C.muted, marginBottom: '16px' }}>
              {allowedZones.length === 0 ? 'No zones selected — visitor will have no zone access.' : `${allowedZones.length} zone(s) selected.`}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <button onClick={() => setStep(2)} style={{ padding: '9px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
              <button onClick={handleConfirm} disabled={saving} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.green, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>{saving ? 'Signing In…' : 'Confirm Sign In'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Badge print helpers ──────────────────────────────────────
const APP_URL_GLOBAL = window.location.origin;

function badgeHtml(card, qrDataUrl) {
  const esc = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const shieldSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L4 6v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6L12 2z" fill="#009688"/></svg>`;
  return `
    <div class="badge">
      <div class="header">
        <div class="logo-area">${shieldSvg}<span class="site-name">${esc(card.siteName || '')}</span></div>
        <div class="visitor-label">VISITOR</div>
      </div>
      <div class="body">
        <div class="fields">
          <div class="field"><div class="fl">Name</div><div class="fv">${esc(card.visitorName || '— Unassigned')}</div></div>
          ${card.visitorCompanyName ? `<div class="field"><div class="fl">Company</div><div class="fv co-name">${esc(card.visitorCompanyName)}</div></div>` : ''}
          <div class="field"><div class="fl">Card No.</div><div class="fv card-num">${esc(card.cardNumber)}</div></div>
        </div>
        <div class="qr-wrap"><img src="${qrDataUrl}" class="qr" /></div>
      </div>
    </div>`;
}

const BADGE_BASE_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #e8e8e8; }
  .badge { background: #fff; width: 85mm; height: 54mm; padding: 3mm 4mm; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.15); page-break-inside: avoid; break-inside: avoid; }
  .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 2mm; border-bottom: 0.5mm solid #009688; padding-bottom: 1.5mm; }
  .logo-area { display: flex; align-items: center; gap: 1.5mm; }
  .site-name { font-size: 8pt; font-weight: 700; color: #111; }
  .visitor-label { font-size: 7pt; font-weight: 800; color: #fff; background: #009688; padding: 1mm 2.5mm; border-radius: 2mm; letter-spacing: 0.04em; }
  .body { display: flex; justify-content: space-between; align-items: center; gap: 2mm; }
  .fields { flex: 1; min-width: 0; }
  .field { margin-bottom: 2.5mm; }
  .field:last-child { margin-bottom: 0; }
  .fl { color: #888; font-size: 5pt; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 0.3mm; }
  .fv { color: #111; font-size: 9pt; font-weight: 700; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .co-name { color: #444; font-size: 8pt; }
  .card-num { color: #009688; font-size: 8pt; }
  .qr-wrap { background: #fff; padding: 1.5mm; border: 0.3mm solid #e8e8e8; border-radius: 1mm; flex-shrink: 0; }
  .qr { width: 20mm; height: 20mm; display: block; }
  @media print { body { background: #fff; } .badge { box-shadow: none; } }
`;

async function printCard(card) {
  const canvas = document.createElement('canvas');
  await qrEncodeToCanvas(`${APP_URL_GLOBAL}/?vis=${card.qrHash}`, canvas, 160);
  const qrDataUrl = canvas.toDataURL();
  const win = window.open('', '_blank', 'width=340,height=220');
  if (!win) return;
  const singleStyles = `
    @page { size: 85mm 54mm; margin: 0; }
    ${BADGE_BASE_STYLES}
    html, body { width: 85mm; height: 54mm; overflow: hidden; background: #fff; }
  `;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Visitor Card — ${card.cardNumber}</title><style>${singleStyles}</style></head><body>${badgeHtml(card, qrDataUrl)}</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 600);
}

async function printCards(cards) {
  const rendered = await Promise.all(cards.map(async card => {
    const canvas = document.createElement('canvas');
    await qrEncodeToCanvas(`${APP_URL_GLOBAL}/?vis=${card.qrHash}`, canvas, 160);
    return badgeHtml(card, canvas.toDataURL());
  }));
  const win = window.open('', '_blank');
  if (!win) return;
  const bulkStyles = `
    @page { size: A4 portrait; margin: 8mm; }
    ${BADGE_BASE_STYLES}
    .page { display: flex; flex-wrap: wrap; gap: 4mm; padding: 0; }
  `;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Visitor Cards</title><style>${bulkStyles}</style></head><body><div class="page">${rendered.join('')}</div></body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 800);
}

// ── Main View ────────────────────────────────────────────────
const STATUSES = ['ALL', 'UNASSIGNED', 'ASSIGNED'];

export default function VisitorCardsView({ visitorCards, sites, zones, visitRequests, scopedSiteId, onBulkAdd, onUpdate, onRemove }) {
  const [filterSite, setFilterSite] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [signInCard, setSignInCard] = useState(null);
  const [acting, setActing] = useState({});

  const effectiveSiteId = scopedSiteId || filterSite;
  const scopedSite = scopedSiteId ? sites.find(s => s.id === scopedSiteId) : null;

  const filtered = visitorCards.filter(vc =>
    (!effectiveSiteId || vc.siteId === effectiveSiteId || vc.site_id === effectiveSiteId) &&
    (filterStatus === 'ALL' || vc.status === filterStatus) &&
    (!search || vc.cardNumber?.toLowerCase().includes(search.toLowerCase()) || vc.card_number?.toLowerCase().includes(search.toLowerCase()) || vc.visitorName?.toLowerCase().includes(search.toLowerCase()) || vc.visitor_name?.toLowerCase().includes(search.toLowerCase()))
  );

  // Normalise snake_case from API to camelCase
  const norm = c => ({
    id: c.id,
    siteId: c.site_id || c.siteId,
    siteName: c.site_name || c.siteName,
    cardNumber: c.card_number || c.cardNumber,
    qrHash: c.qr_hash || c.qrHash,
    status: c.status,
    visitorName: c.visitor_name || c.visitorName,
    visitorCompanyName: c.visitor_company_name || c.visitorCompanyName || '',
    assignedPersonnelId: c.assigned_personnel_id || c.assignedPersonnelId,
    allowedZoneIds: c.allowed_zone_ids || c.allowedZoneIds || [],
    currentZoneId: c.current_zone_id || c.currentZoneId,
    emergencyStatus: c.emergency_status || c.emergencyStatus || 'NOT_RESPONDED',
    isActive: c.is_active !== undefined ? c.is_active : true,
  });

  async function handleSignOut(c) {
    if (!window.confirm(`Sign out ${c.visitorName || c.cardNumber}?`)) return;
    setActing(a => ({ ...a, [c.id]: 'out' }));
    try { const updated = await signOutVisitorCard(c.id); onUpdate(updated); }
    catch (e) { alert(e.response?.data?.error || 'Failed to sign out'); }
    setActing(a => ({ ...a, [c.id]: null }));
  }

  async function handleMarkSafe(c) {
    setActing(a => ({ ...a, [c.id]: 'safe' }));
    try { const updated = await markVisitorSafe(c.id); onUpdate(updated); }
    catch (e) { alert(e.response?.data?.error || 'Failed to mark safe'); }
    setActing(a => ({ ...a, [c.id]: null }));
  }

  async function handleMarkEvacuated(c) {
    setActing(a => ({ ...a, [c.id]: 'evac' }));
    try { const updated = await markVisitorEvacuated(c.id); onUpdate(updated); }
    catch (e) { alert(e.response?.data?.error || 'Failed to mark evacuated'); }
    setActing(a => ({ ...a, [c.id]: null }));
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete card ${c.cardNumber}? This cannot be undone.`)) return;
    try { await deleteVisitorCard(c.id); onRemove(c.id); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle' };

  return (
    <div style={{ padding: '24px', color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <CreditCard size={20} color={C.teal} />
        <div>
          <div style={{ fontSize: '17px', fontWeight: 700 }}>Visitor Cards{scopedSite ? ` — ${scopedSite.name}` : ''}</div>
          <div style={{ fontSize: '12px', color: C.muted }}>Manage and assign pre-created visitor access cards</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {filtered.length > 0 && (
            <button onClick={() => printCards(filtered.map(raw => { const c = norm(raw); return c; }))} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Printer size={15} /> Print All ({filtered.length})
            </button>
          )}
          <button onClick={() => setShowCreate(true)} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.teal}55`, background: C.teal + '22', color: C.teal, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={15} /> Create Cards
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {!scopedSiteId && (
          <select style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit' }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: '4px' }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${filterStatus === s ? C.teal : C.border}`, background: filterStatus === s ? C.teal + '22' : 'transparent', color: filterStatus === s ? C.teal : C.muted, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{s}</button>
          ))}
        </div>
        <input placeholder="Search card or visitor…" style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit', flex: 1, minWidth: '160px' }} value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {(scopedSiteId
                ? ['Card', 'QR', 'Visitor', 'Allowed Zones', 'Status', 'Location', 'Actions']
                : ['Card', 'QR', 'Site', 'Visitor', 'Allowed Zones', 'Status', 'Location', 'Actions']
              ).map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={scopedSiteId ? 7 : 8} style={{ ...td, textAlign: 'center', color: C.muted, padding: '32px' }}>No visitor cards found</td></tr>
            ) : filtered.map(raw => {
              const c = norm(raw);
              const siteZones = zones.filter(z => (z.siteId || z.site_id) === c.siteId);
              const allowedNames = c.allowedZoneIds.map(zid => zones.find(z => z.id === zid)?.name || zid).filter(Boolean);
              const currentZone = zones.find(z => z.id === c.currentZoneId);
              const emColor = EMERGENCY_COLORS[c.emergencyStatus] || C.muted;
              return (
                <tr key={c.id}>
                  <td style={{ ...td, fontWeight: 700, color: C.teal }}>{c.cardNumber}</td>
                  <td style={td}>
                    <RealQRCode value={`${APP_URL_GLOBAL}/?vis=${c.qrHash}`} size={40} darkColor="#e2e8f0" lightColor="#131a2b" />
                  </td>
                  {!scopedSiteId && <td style={{ ...td, color: C.subtle, fontSize: '12px' }}>{c.siteName}</td>}
                  <td style={td}>
                    {c.status === 'ASSIGNED'
                      ? <div><div style={{ fontWeight: 600 }}>{c.visitorName}</div></div>
                      : <span style={{ color: C.muted, fontStyle: 'italic' }}>— Unassigned</span>}
                  </td>
                  <td style={{ ...td, fontSize: '11px', color: C.subtle, maxWidth: '160px' }}>
                    {allowedNames.length > 0 ? allowedNames.join(', ') : <span style={{ color: C.muted }}>None</span>}
                  </td>
                  <td style={td}>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: emColor + '22', color: emColor }}>
                      {c.emergencyStatus}
                    </span>
                  </td>
                  <td style={{ ...td, fontSize: '12px', color: C.subtle }}>{currentZone?.name || '—'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {c.status === 'UNASSIGNED' && (
                        <button onClick={() => setSignInCard({ ...raw, ...c, siteId: c.siteId })} style={{ padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.green}55`, background: C.green + '22', color: C.green, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LogIn size={12} /> Sign In
                        </button>
                      )}
                      {c.status === 'ASSIGNED' && (
                        <button onClick={() => handleSignOut(c)} disabled={acting[c.id] === 'out'} style={{ padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.amber}55`, background: C.amber + '22', color: C.amber, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <LogOut size={12} /> Sign Out
                        </button>
                      )}
                      {c.emergencyStatus !== 'SAFE' && (
                        <button onClick={() => handleMarkSafe(c)} disabled={acting[c.id] === 'safe'} style={{ padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.green}44`, background: C.green + '11', color: C.green, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircle size={12} /> Safe
                        </button>
                      )}
                      <button onClick={() => printCard(c)} title="Print Card" style={{ padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.blue}44`, background: C.blue + '11', color: C.blue, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Printer size={12} />
                      </button>
                      {c.status === 'UNASSIGNED' && (
                        <button onClick={() => handleDelete(c)} style={{ padding: '5px 9px', borderRadius: '7px', border: `1px solid ${C.red}44`, background: C.red + '11', color: C.red, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CardCreateModal
          sites={scopedSiteId && scopedSite ? [scopedSite] : sites}
          siteId={scopedSiteId || filterSite}
          onSave={created => { onBulkAdd(created); setShowCreate(false); }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {signInCard && (
        <SignInModal
          card={signInCard}
          siteId={signInCard.siteId}
          zones={zones}
          assignedPersonnelIds={new Set(visitorCards.filter(vc => (vc.status === 'ASSIGNED') && (vc.assignedPersonnelId || vc.assigned_personnel_id)).map(vc => vc.assignedPersonnelId || vc.assigned_personnel_id))}
          onSave={updated => { onUpdate(updated); setSignInCard(null); }}
          onClose={() => setSignInCard(null)}
        />
      )}
    </div>
  );
}
